import React, { useCallback, useContext, useMemo } from 'react';
import SelectContextItem from '@components/SelectContext/SelectContextItem';
import {
  ApplicationsConstants,
  DefaultApplicationsConstants,
} from '@utils/applicationsConstants';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import useSwr from './useSwr';

interface OptionsProps {
  label: string;
  value: number;
  filterProps: string;
}

export interface UseCustomersReturnType {
  customersData: any[];
  customersDataIncludeMe: any[];
  customerLoggedName: string;
  customerLogged: { [key: string]: any };
  customerApplications: ApplicationsConstants[] | undefined;
  isCustomerFinanciallyBlocked: boolean;
  getCustomerById: (customerId?: string | number) => any;
  customersSelectOptions: OptionsProps[];
  customersSelectOptionsIncludeMe: OptionsProps[];
  mutateAllCustomerData: () => void;
}

const useCustomers = (
  hasCustomersRoles: boolean,
  hasRoles: boolean,
): UseCustomersReturnType => {
  const { keycloak } = useContext(KeycloakContext);
  const { data: customersDataIncludeMe, mutate: mutateCustomersDataIncludeMe } =
    useSwr(hasCustomersRoles ? '/customers/' : null);

  const { data: customersData, mutate: mutateCustomersData } = useSwr(
    hasCustomersRoles ? '/customers/?myself=false' : null,
  );

  const { data: customerLogged } = useSwr(hasRoles ? `/customers/self` : null);

  const customerApplications = useMemo(():
    | ApplicationsConstants[]
    | undefined => {
    if (!customerLogged) {
      return undefined;
    }
    const customerType = keycloak.idTokenParsed?.customerType;
    const isRoot = customerType === 'ROOT';
    const listProducts = customerLogged?.listContractTypes?.map(
      (item: string) => {
        let defaultName = item;
        if (item === 'API') {
          defaultName = ApplicationsConstants.API;
        }
        return defaultName.toLowerCase();
      },
    );
    if (isRoot) {
      return Object.values(ApplicationsConstants);
    }

    const defaultApps = Object.values(DefaultApplicationsConstants);

    return (
      listProducts ? [...listProducts, ...defaultApps] : defaultApps
    )?.filter(app => app !== 'mot'); // removed fake app
  }, [customerLogged, keycloak.idTokenParsed?.customerType]);

  const isCustomerFinanciallyBlocked = useMemo(
    () => customerLogged?.financialBlock,
    [customerLogged],
  );

  const customerLoggedName = useMemo(
    () => customerLogged?.name,
    [customerLogged],
  );

  const customersSelectOptions = useMemo(
    () =>
      customersData?.map(
        (customer: {
          name: string;
          id: number;
          tradingName: string;
          cpfCnpj: string;
        }) => ({
          value: customer.id,
          label: <SelectContextItem cnpj={customer.cpfCnpj} {...customer} />,
          filterProps: `${customer.name}${customer.tradingName ?? ''}${
            customer.cpfCnpj
          }${customer.id}`,
        }),
      ),
    [customersData],
  );

  const customersSelectOptionsIncludeMe = useMemo(
    () =>
      customersDataIncludeMe?.map(
        (customer: {
          name: string;
          id: number;
          tradingName: string;
          cpfCnpj: string;
        }) => ({
          value: customer.id,
          label: <SelectContextItem cnpj={customer.cpfCnpj} {...customer} />,
          filterProps: `${customer.name}${customer.tradingName ?? ''}${
            customer.cpfCnpj
          }${customer.id}`,
        }),
      ),
    [customersDataIncludeMe],
  );

  const mutateAllCustomerData = () => {
    mutateCustomersDataIncludeMe();
    mutateCustomersData();
  };

  const getCustomerById = useCallback(
    (customerId?: string | number) =>
      customersData?.find(
        (cstData: { id: number }) => cstData.id === customerId,
      ),
    [customersData],
  );

  return {
    customersData,
    customersDataIncludeMe,
    customersSelectOptions,
    customerLoggedName,
    customerLogged,
    customerApplications,
    isCustomerFinanciallyBlocked,
    getCustomerById,
    customersSelectOptionsIncludeMe,
    mutateAllCustomerData,
  };
};

export default useCustomers;
